import React from 'react';
import {Box, IconButton, Typography} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import SortableItem from './SortableItem';
import {SortableContext, verticalListSortingStrategy} from '@dnd-kit/sortable';
import AnnotationItem from './AnnotationItem';

function AnnotationGroup({
                             id,
                             groupId,
                             groupName,
                             annotations,
                             groupAnnotationOrder,
                             selectedAnnotations,
                             renderEditableField,
                             handleDelete,
                             handleGroupMenuOpen,
                             onTablePatch,
                             schedulePeek,
                             cancelPeek,
                             isEditingId,
                             onHover,
                             tableWarningText
                         }) {
    const childIds = groupAnnotationOrder[groupId] || [];
    return (
        <SortableItem key={id} id={id}>
            <Box
                mt={2}
                p={1}
                border="1px solid"
                borderColor="divider"
                borderRadius={1.5}
                bgcolor="background.paper"
                data-id={id}
                sx={{
                    position: 'relative',
                    transition: 'transform 0.2s ease',
                    willChange: 'transform',
                    boxShadow: 1,
                    '&:hover': {boxShadow: 2, bgcolor: 'action.hover'}
                }}
                onClick={(e) => {
                    // Click on the group container jiggles ALL children
                    if (e.defaultPrevented) return;
                    // If the group name itself is being edited, do nothing
                    if (isEditingId?.(`group_${groupId}`)) return;
                    // Filter out children currently being edited
                    const peekList = childIds.filter((cid) => !isEditingId?.(cid));
                    if (peekList.length) schedulePeek(peekList);
                }}
                onDoubleClickCapture={cancelPeek}  // ensure no jiggle on double-click
            >
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    onClick={(e) => {
                        // Header click also jiggles group (nice affordance)
                        if (e.defaultPrevented) return;
                        if (isEditingId?.(`group_${groupId}`)) return;
                        const peekList = childIds.filter((cid) => !isEditingId?.(cid));
                        if (peekList.length) schedulePeek(peekList);
                    }}
                    onDoubleClickCapture={cancelPeek}
                >                    {renderEditableField(`group_${groupId}`, null, groupName, 'name', true, true)}
                </Box>
                <IconButton
                    size="small"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleGroupMenuOpen(e, groupId);
                    }}
                    sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        zIndex: 10,
                        backgroundColor: 'white',
                        '&:hover': {backgroundColor: '#eee'},
                        pointerEvents: 'auto',
                    }}
                >
                    <MoreVertIcon fontSize="small"/>
                </IconButton>

                <SortableContext
                    id={`group-sortable-${groupId}`}
                    items={groupAnnotationOrder[groupId] || []}
                    strategy={verticalListSortingStrategy}
                >
                    {(groupAnnotationOrder[groupId] || []).map((childId) => {
                        const annotation = selectedAnnotations.find(a => a.__uid === childId);
                        if (!annotation || annotation.groupId !== groupId) return null;

                        return (
                            <SortableItem key={childId} id={childId}>
                                <Box
                                    mt={1}
                                    p={1}
                                    border="1px solid"
                                    borderColor="divider"
                                    borderRadius={1}
                                    bgcolor="background.paper"
                                    sx={{
                                        transition: 'transform 200ms ease',
                                        willChange: 'transform',
                                        '&:active': {cursor: 'grabbing'},
                                        '&:hover': {bgcolor: 'action.hover'}
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();        // don't also trigger the group handler
                                        if (!isEditingId?.(childId)) {
                                            schedulePeek(childId);    // jiggle just this child
                                        }
                                    }}
                                    onDoubleClickCapture={cancelPeek}
                                    onMouseEnter={() => onHover?.(childId)}
                                    onMouseLeave={() => onHover?.(null)}
                                >
                                    <AnnotationItem
                                        annotation={annotation}
                                        renderEditableField={(fieldKey, fieldValue, inline = false, bold = false) => {
                                            let safeValue = fieldValue;
                                            if (fieldValue && typeof fieldValue === 'object' && 'text' in fieldValue) {
                                                safeValue = fieldValue.text;
                                            }
                                            return renderEditableField(childId, null, safeValue, fieldKey, inline, bold);
                                        }}
                                        onTablePatch={(patch) => onTablePatch?.(childId, patch)}
                                        tableWarningText={tableWarningText}
                                    />
                                    {/*                                    <IconButton
                                        size="small"
                                        aria-label="Delete"
                                        onClick={() => handleDelete(annotation)}
                                        sx={{float: 'right', mt: -4}}
                                    >
                                        <DeleteIcon fontSize="small"/>
                                    </IconButton>*/}
                                </Box>
                            </SortableItem>
                        );
                    })}
                </SortableContext>
            </Box>
        </SortableItem>
    );
}

export default AnnotationGroup;
